import { useState, useEffect, useMemo } from 'react';
import { useToast } from './Toast';
import { getDriveShares, setDriveShares, drivePeople } from '../api/drive';

const avatar = (content) => (
  <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'color-mix(in srgb, var(--accent) 15%, transparent)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, flex: 'none' }}>{content}</div>
);

export default function DriveShareDialog({ file, onClose }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [owner, setOwner] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [shares, setShares] = useState([]); // {kind:'user'|'dept', id?, dept?, name, role}
  const [people, setPeople] = useState({ users: [], departments: [] });
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([getDriveShares(file.id), drivePeople().catch(() => ({ users: [], departments: [] }))]);
        setOwner(s.owner); setCanManage(!!s.canManage);
        setShares((s.shares || []).map((x) => (x.kind === 'user' ? { kind: 'user', id: x.id, name: x.name, role: x.role, isPartner: x.isPartner } : { kind: 'dept', dept: x.dept, name: x.dept, role: x.role })));
        setPeople(p);
      } catch (e) {
        toast('Không tải được thông tin chia sẻ', 'danger');
      } finally {
        setLoading(false);
      }
    })();
  }, [file.id, toast]);

  const has = (kind, key) => shares.some((s) => s.kind === kind && (kind === 'user' ? s.id === key : s.dept === key));
  const addUser = (u) => { if (!has('user', u.id) && u.id !== owner?.id) setShares((p) => [...p, { kind: 'user', id: u.id, name: u.name, role: 'view', isPartner: u.isPartner }]); setQ(''); };
  const addDept = (d) => { if (!has('dept', d)) setShares((p) => [...p, { kind: 'dept', dept: d, name: d, role: 'view' }]); setQ(''); };
  const setRole = (i, role) => setShares((p) => p.map((s, j) => (j === i ? { ...s, role } : s)));
  const remove = (i) => setShares((p) => p.filter((_, j) => j !== i));

  const sug = useMemo(() => {
    if (!q.trim()) return { users: [], depts: [] };
    const t = q.toLowerCase();
    return {
      depts: (people.departments || []).filter((d) => d.toLowerCase().includes(t) && !has('dept', d)).slice(0, 5),
      users: (people.users || []).filter((u) => (u.name || '').toLowerCase().includes(t) && !has('user', u.id) && u.id !== owner?.id).slice(0, 8),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, people, shares, owner]);

  const save = async () => {
    setSaving(true);
    try {
      await setDriveShares(file.id, shares.map((s) => (s.kind === 'user' ? { kind: 'user', user: s.id, role: s.role } : { kind: 'dept', dept: s.dept, role: s.role })));
      toast('Đã lưu chia sẻ');
      onClose();
    } catch (e) {
      toast(e?.response?.data?.message || 'Không lưu được', 'danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div className="card" style={{ width: 460, maxWidth: '92vw', maxHeight: '85vh', overflow: 'auto', padding: 18 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}><i className="ti ti-share" /> Chia sẻ “{file.name}”</h3>
        {loading ? (
          <div className="empty">Đang tải…</div>
        ) : (
          <>
            {canManage && (
              <div style={{ position: 'relative', marginBottom: 14 }}>
                <div className="row" style={{ gap: 6, border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px' }}>
                  <i className="ti ti-user-plus" style={{ color: 'var(--text3)' }} />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Thêm người hoặc phòng ban…" style={{ border: 'none', background: 'none', outline: 'none', width: '100%', fontSize: 13 }} />
                </div>
                {(sug.users.length > 0 || sug.depts.length > 0) && (
                  <div className="card" style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 10, maxHeight: 220, overflow: 'auto', padding: 4 }}>
                    {sug.depts.map((d) => (<button key={'d' + d} className="btn ghost sm" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => addDept(d)}><i className="ti ti-users" /> {d} <span style={{ color: 'var(--text3)', fontSize: 11 }}>· phòng ban</span></button>))}
                    {sug.users.map((u) => (<button key={'u' + u.id} className="btn ghost sm" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => addUser(u)}><i className={'ti ' + (u.isPartner ? 'ti-briefcase' : 'ti-user')} /> {u.name}{u.isPartner ? <span style={{ color: 'var(--accent)', fontSize: 11 }}> · Đối tác</span> : u.dept ? <span style={{ color: 'var(--text3)', fontSize: 11 }}> · {u.dept}</span> : null}</button>))}
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {owner && (
                <div className="row" style={{ gap: 10 }}>
                  {avatar((owner.name || '?')[0])}
                  <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 13 }}>{owner.name}</div></div>
                  <span style={{ fontSize: 12, color: 'var(--text2)' }}>Chủ sở hữu</span>
                </div>
              )}
              {shares.map((s, i) => (
                <div key={i} className="row" style={{ gap: 10 }}>
                  {avatar(s.kind === 'dept' ? <i className="ti ti-users" /> : (s.name || '?')[0])}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
                    {s.kind === 'dept' && <div style={{ fontSize: 11, color: 'var(--text3)' }}>Phòng ban</div>}
                    {s.kind === 'user' && s.isPartner && <div style={{ fontSize: 11, color: 'var(--accent)' }}>Đối tác</div>}
                  </div>
                  {canManage ? (
                    <>
                      <select value={s.role} onChange={(e) => setRole(i, e.target.value)} style={{ fontSize: 12, padding: '3px 6px', borderRadius: 6, border: '1px solid var(--border)' }}>
                        <option value="view">Xem</option>
                        <option value="edit">Sửa</option>
                      </select>
                      <button className="btn ghost sm" style={{ padding: 4 }} onClick={() => remove(i)} aria-label="Gỡ"><i className="ti ti-x" /></button>
                    </>
                  ) : (
                    <span style={{ fontSize: 12, color: 'var(--text2)' }}>{s.role === 'edit' ? 'Sửa' : 'Xem'}</span>
                  )}
                </div>
              ))}
              {shares.length === 0 && <div style={{ fontSize: 12, color: 'var(--text3)' }}>Chưa chia sẻ với ai — chỉ mình bạn thấy.</div>}
            </div>
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button className="btn ghost" onClick={onClose}>Đóng</button>
              {canManage && <button className="btn" onClick={save} disabled={saving}>{saving ? 'Đang lưu…' : 'Lưu'}</button>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
